import type { AgentMode, EventType, HookType } from '@/features/events/shared/types/enums'
import { agentModeFrom, eventTypeFrom, hookTypeFrom } from '@/features/events/shared/types/enums'
import type { EncryptionStatus } from '@/features/events/shared/types/EncryptionInfo'
import type { Timestamp, UUID } from '@/common/types'
import type {
  ConversationEventPayload,
  FileChanges,
  NotificationContext,
} from '@/infra/sse/conversation/payloads'
import type { MetadataPayload, Telemetry, UserHostDetails } from '@/infra/sse/shared/payloads'
import type {
  ClaudeSession,
  ConversationTurnEntry,
} from '@/features/events/conversation/state/types'

export interface EventMetadata {
  readonly timestamp: Timestamp
  readonly userExternalId: UUID
  readonly claudeSessionId: UUID
  readonly userPromptId: UUID
  readonly eventId: UUID
  readonly hookType: HookType | null
  readonly transcriptPath: string
  readonly contextWorkDirectory: string
  readonly agentMode: AgentMode
  readonly dangerouslySkipPermissions: boolean | undefined
  readonly autoModeSupported: boolean | undefined
}

export interface ConversationEventDetails {
  readonly notificationMessage: string | null
  readonly toolName: string | null
  readonly notificationContext: NotificationContext | null
  readonly agentMode: AgentMode
  readonly agentResponse: string | null
  readonly fileChanges: FileChanges | null
  readonly userPrompt: string | null
  readonly eventType: EventType
  readonly eventMetadata: EventMetadata
  readonly userHostDetails: UserHostDetails
  readonly telemetry: Telemetry
  readonly encryption: EncryptionStatus
}

export function buildClaudeCodeEventDetailsFrom(
  payload: ConversationEventPayload,
): ConversationEventDetails {
  const eventMetadata = eventMetadataFrom(payload.eventMetadata)
  const notificationContext = payload.notificationContext ?? null

  const eventType = eventTypeFrom(payload.eventType)

  return {
    notificationMessage: payload.notificationMessage,
    toolName: payload.toolName ?? null,
    notificationContext,
    agentMode: eventMetadata.agentMode,
    agentResponse: payload.agentResponse,
    fileChanges: payload.fileChanges ?? null,
    userPrompt: payload.userPrompt ?? null,
    eventType,
    eventMetadata: eventMetadata,
    userHostDetails: payload.hostDetails,
    telemetry: {
      tmuxSession: payload.telemetry?.tmuxSession || null,
    },
    encryption: payload.encryption,
  }
}

export function rebuildClaudeCodeEventDetailsFrom(
  event: ConversationTurnEntry,
  session: ClaudeSession,
): ConversationEventDetails {
  const userPromptId = getUserPromptIdFor(event, session)

  return {
    notificationMessage: event.notificationMessage,
    toolName: event.toolName,
    notificationContext: event.notificationContext,
    agentMode: event.agentMode,
    agentResponse: event.agentResponse,
    fileChanges: event.fileChanges,
    userPrompt: event.userPrompt,
    eventType: event.eventType,
    eventMetadata: {
      timestamp: event.timestamp,
      userExternalId: session.userExternalId,
      claudeSessionId: session.sessionId,
      userPromptId: userPromptId,
      eventId: event.eventId,
      hookType: event.hookType,
      transcriptPath: session.transcriptPath,
      contextWorkDirectory: session.contextWorkDirectory,
      agentMode: event.agentMode,
      dangerouslySkipPermissions: session.dangerouslySkipPermissions,
      autoModeSupported: session.autoModeSupported,
    },
    userHostDetails: session.userHostDetails,
    telemetry: {
      tmuxSession: session.tmuxSession,
    },
    encryption: event.encryption,
  }
}

function eventMetadataFrom(payload: MetadataPayload): EventMetadata {
  return {
    timestamp: payload.timestamp,
    userExternalId: payload.userExternalId,
    claudeSessionId: payload.claudeSessionId,
    userPromptId: payload.userPromptId,
    eventId: payload.eventId,
    hookType: hookTypeFrom(payload.hookType),
    transcriptPath: payload.transcriptPath,
    contextWorkDirectory: payload.contextWorkDirectory,
    agentMode: agentModeFrom(payload.agentMode),
    dangerouslySkipPermissions: payload.dangerouslySkipPermissions,
    autoModeSupported: payload.autoModeSupported,
  }
}

function getUserPromptIdFor(event: ConversationTurnEntry, session: ClaudeSession): string {
  for (const [userPromptId, turn] of session.conversationTurns) {
    if (turn.events.some((e) => e.eventId === event.eventId)) {
      return userPromptId
    }
  }
  return ''
}
